"use client";

import { useMemo, useState } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import FiltersPanelClause from "../FiltersPanelClause";
import ClauseGroupTreemap, {
  getClauseGroupDisplayName,
} from "../charts/ClauseGroupTreemap";
import clauseBondLoan from "@/_data/dashboard/df_clause_bond_loan.json";
import { cleanGroup, docsByField } from "../utils";
import ClauseDonutChart from "../charts/ClauseDonutChart";
import type { ClauseRow, ClauseFilters } from "../types";

// Collect all DOCIDs whose Loan_or_Bond value matches the selected types
function getMatchingDocIds(
  rows: ClauseRow[],
  selectedTypes: string[],
): Set<string> | null {
  if (selectedTypes.length === 0) return null;
  const selected = new Set(selectedTypes.map((t) => t.trim()));
  const matched = new Set<string>();
  for (const row of rows) {
    const docId = String(row.DOCID ?? "").trim();
    const loanOrBond = String(row.Loan_or_Bond ?? "")
      .replace(/^"|"$/g, "")
      .trim();
    if (docId && selected.has(loanOrBond)) matched.add(docId);
  }
  return matched;
}

const CLAUSE_GROUP_DISPLAY_ORDER: string[] = (
  clauseBondLoan as { clause_group: string; type: string }[]
).map((r) => r.clause_group);

const DONUT_PALETTE = [
  "#565988",
  "#de7d47",
  "#c0415c",
  "#7cc286",
  "#9ca3af",
  "#408A71",
  "#573a53",
  "#4f86df",
  "#305ac3",
  "#84a8dc",
];

const BLUE_PALETTE = [
  "#012852", // darkest
  "#01497c",
  "#01497c", // matching to simplify palette
  "#0066AA",
  "#0066AA", // matching to simplify palette
  "#5196c4", // lightest
];

type ClauseDeepDiveTabProps = {
  clauseRows: ClauseRow[];
};

export default function ClauseDeepDiveTab({
  clauseRows,
}: ClauseDeepDiveTabProps) {
  // Build filter option lists from data
  const creditorRegionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.CreditorGeographicRegion ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const borrowerRegionOptions = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.BorrowerGeographicRegion ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const creditorTypeColumns = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.pCreditorType ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const denominationColumns = useMemo(
    () =>
      Array.from(
        new Set(
          clauseRows
            .map((r) => String(r.inferred_denominations_readable ?? "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [clauseRows],
  );
  const minYear = useMemo(
    () =>
      Math.min(
        ...clauseRows
          .map((r) => Number(r.DocumentYear))
          .filter((y) => !isNaN(y) && y > 0),
      ),
    [clauseRows],
  );
  const maxYear = useMemo(
    () =>
      Math.max(
        ...clauseRows
          .map((r) => Number(r.DocumentYear))
          .filter((y) => !isNaN(y) && y > 0),
      ),
    [clauseRows],
  );

  const [filters, setFilters] = useState<ClauseFilters>({
    selectedInstrumentTypes: [],
    selectedCreditorRegions: [],
    selectedBorrowerRegions: [],
    selectedCreditorTypes: [],
    selectedDenominations: [],
    yearRange: [minYear, maxYear],
    completeDataOnly: false,
  });

  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  // Pre filter rows by all active filters
  const filteredRows = useMemo(() => {
    const matchingDocIds = getMatchingDocIds(
      clauseRows,
      filters.selectedInstrumentTypes,
    );
    return clauseRows.filter((row) => {
      if (matchingDocIds) {
        const docId = String(row.DOCID ?? "").trim();
        if (!matchingDocIds.has(docId)) return false;
      }
      if (filters.selectedCreditorRegions.length > 0) {
        const region = String(row.CreditorGeographicRegion ?? "").trim();
        if (!filters.selectedCreditorRegions.includes(region)) return false;
      }
      if (filters.selectedBorrowerRegions.length > 0) {
        const region = String(row.BorrowerGeographicRegion ?? "").trim();
        if (!filters.selectedBorrowerRegions.includes(region)) return false;
      }
      if (filters.selectedCreditorTypes.length > 0) {
        const type = String(row.pCreditorType ?? "").trim();
        if (!filters.selectedCreditorTypes.includes(type)) return false;
      }
      if (filters.selectedDenominations.length > 0) {
        const denom = String(row.inferred_denominations_readable ?? "").trim();
        if (!filters.selectedDenominations.includes(denom)) return false;
      }
      const year = Number(row.DocumentYear);
      if (
        !isNaN(year) &&
        (year < filters.yearRange[0] || year > filters.yearRange[1])
      )
        return false;
      if (filters.completeDataOnly) {
        if (String(row.complete_data ?? "").trim() !== "True") return false;
      }
      return true;
    });
  }, [clauseRows, filters]);

  // Build unique clause groups from filtered rows with doc counts
  const groupOrder = useMemo(() => {
    const fromData = CLAUSE_GROUP_DISPLAY_ORDER.filter((g) =>
      filteredRows.some((r) => cleanGroup(r.clause_group) === g),
    );
    const extra = Array.from(
      new Set(
        filteredRows.map((r) => cleanGroup(r.clause_group)).filter(Boolean),
      ),
    )
      .filter((g) => !fromData.includes(g))
      .sort();
    return [...fromData, ...extra];
  }, [filteredRows]);

  // Ensure selected group is still valid after filtering
  const effectiveGroup =
    selectedGroup && groupOrder.includes(selectedGroup) ? selectedGroup : null;

  // Rows for the selected group
  const groupRows = useMemo(
    () =>
      effectiveGroup
        ? filteredRows.filter(
            (r) => cleanGroup(r.clause_group) === effectiveGroup,
          )
        : [],
    [filteredRows, effectiveGroup],
  );

  // Data for donut charts
  const instrData = useMemo(() => {
    const docSets = docsByField(groupRows, "Loan_or_Bond");
    return Array.from(docSets.entries())
      .map(([name, set]) => ({ name, value: set.size }))
      .sort((a, b) => b.value - a.value);
  }, [groupRows]);

  const creditorTypeData = useMemo(() => {
    const docSets = docsByField(groupRows, "pCreditorType");
    return Array.from(docSets.entries())
      .map(([name, set]) => ({ name, value: set.size }))
      .sort((a, b) => b.value - a.value);
  }, [groupRows]);

  const hasFilteredResults = filteredRows.length > 0;

  return (
    <Stack spacing={2.5}>
      {/* Intro */}
      <Card sx={{ px: "1rem", py: "1rem" }}>
        <CardContent>
          <Typography sx={{ fontSize: "1rem", color: "#374151" }}>
            The <strong>#PublicDebtIsPublic</strong> tags allow the public to
            search and compare key contractual terms in sovereign debt
            documents. Use the interactive visualizations to better understand
            the distribution of specific clauses identified in the{" "}
            <strong>#PublicDebtIsPublic</strong> documents.
          </Typography>
        </CardContent>
      </Card>

      {/* Group selector */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#363636", mb: 1.5 }}
          >
            Term Types
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 6,
            }}
          >
            {hasFilteredResults ? (
              groupOrder.map((group) => {
                const isActive = effectiveGroup === group;
                return (
                  <Box
                    key={group}
                    onClick={() => setSelectedGroup(isActive ? null : group)}
                    sx={{
                      cursor: "pointer",
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: isActive ? "#012852" : "#d1d5db",
                      bgcolor: isActive ? "#012852" : "#f9fafb",
                      color: isActive ? "#fff" : "#374151",
                      transition: "all 0.15s",
                      "&:hover": {
                        borderColor: "#012852",
                        bgcolor: isActive ? "#012852" : "#eff6ff",
                      },
                      userSelect: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {getClauseGroupDisplayName(group)}
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Typography sx={{ color: "#6b7280", fontSize: "0.95rem" }}>
                No results match the selected filters. Try removing or adjusting
                one or more filters.
              </Typography>
            )}
          </Box>

          <FiltersPanelClause
            filters={filters}
            setFilters={setFilters}
            creditorRegionOptions={creditorRegionOptions}
            borrowerRegionOptions={borrowerRegionOptions}
            creditorTypeColumns={creditorTypeColumns}
            denominationColumns={denominationColumns}
            minYear={minYear}
            maxYear={maxYear}
            sticky={false}
            horizontal
            noCard
          />
        </CardContent>
      </Card>

      {/* Deep dive content */}
      {!hasFilteredResults ? (
        <Card>
          <CardContent>
            <Typography
              sx={{
                color: "#6b7280",
                fontSize: "0.95rem",
                textAlign: "center",
                py: 3,
              }}
            >
              No results match the selected filters. Try removing or adjusting
              one or more filters.
            </Typography>
          </CardContent>
        </Card>
      ) : !effectiveGroup ? (
        <Card>
          <CardContent>
            <Typography
              sx={{
                color: "#6b7280",
                fontSize: "0.95rem",
                textAlign: "center",
                py: 3,
              }}
            >
              Click a group above to explore its details.
            </Typography>
            <Typography
              sx={{
                mt: 2,
                fontSize: "0.85rem",
                color: "#6b7280",
                textAlign: "right",
              }}
            >
              Last updated: 09/28/2026
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {effectiveGroup && groupRows.length > 0 && (
        <>
          {/* Group header */}
          <Card sx={{ bgcolor: "#012852", color: "#fff" }}>
            <CardContent sx={{ py: "1rem !important" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#fff", fontSize: "1.1rem" }}
              >
                {getClauseGroupDisplayName(effectiveGroup)}{" "}
              </Typography>
            </CardContent>
          </Card>

          {/* Sub-clause Treemap */}
          <Card>
            <ClauseGroupTreemap
              title="Terms"
              rows={groupRows}
              groupField="clause_hr"
              palette={BLUE_PALETTE}
              colorByRank
            />
          </Card>

          {/* Donut charts */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Card>
              <CardContent>
                <ClauseDonutChart
                  title="Instrument Type"
                  data={instrData}
                  palette={DONUT_PALETTE}
                  legendBottom={0}
                />
                <Typography
                  sx={{
                    mt: 4,
                    mb: 0,
                    fontSize: "0.7rem",
                    color: "#6b7280",
                    textAlign: "right",
                  }}
                >
                  Last updated: 09/28/2026
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <ClauseDonutChart
                  title="Creditor Type"
                  data={creditorTypeData}
                  palette={DONUT_PALETTE}
                />
                <Typography
                  sx={{
                    mt: 4,
                    mb: 0,
                    fontSize: "0.7rem",
                    color: "#6b7280",
                    textAlign: "right",
                  }}
                >
                  Last updated: 09/28/2026
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </>
      )}
    </Stack>
  );
}
